import React from 'react';
import { Quote, Users, Calendar, Heart } from 'lucide-react';

// Testimonial data - authentic stories from Ruben's journey
const testimonials = [
  {
    id: 1,
    name: "Dr. Karel Novotný",
    role: "Professor of Structural Engineering, Czech Technical University",
    era: "The Early Years",
    period: "1993–1995",
    relationship: "Academic Mentor",
    image: null,
    testimonial: "I remember Ruben as a student who asked impossible questions. While others calculated load factors, he wanted to know how structures would perform in 100 years. That wasn't arrogance—it was genuine curiosity about legacy. When his first consulting venture didn't work out, he didn't see failure. He saw education. That's the difference between engineers who build projects and engineers who build futures.",
    highlight: "He didn't see failure. He saw education."
  },
  {
    id: 2,
    name: "Tomáš Dvořák",
    role: "Senior Bridge Engineer, Prague Municipal Authority",
    era: "The Project Years",
    period: "1995–1998",
    relationship: "Engineering Colleague",
    image: null,
    testimonial: "Working alongside Ruben during the post-socialist infrastructure rebuilds taught me what real leadership looks like. He wasn't the loudest voice in planning meetings, but when he spoke, the room listened. I watched him coordinate teams across three cities, manage conflicting stakeholder interests, and still find time to mentor junior engineers. The bridges we rehabilitated together are still standing—not just structurally, but as examples of what happens when vision meets discipline.",
    highlight: "When he spoke, the room listened."
  },
  {
    id: 3,
    name: "Anna Svobodová",
    role: "Co-Founder & Operations Director, Early Redspire Team",
    era: "The Founding",
    period: "1998–2003",
    relationship: "Business Partner",
    image: null,
    testimonial: "Starting Redspire wasn't glamorous. It was Ruben, myself, and two desks in a Prague office that leaked when it rained. But Ruben had this unshakeable belief that Europe needed a new kind of engineering consultancy—one that thought in generations, not quarters. I remember the first major contract we won. Everyone wanted to celebrate. Ruben's first question was: 'How do we make sure we're still proud of this work in 2050?' That question became our culture.",
    highlight: "How do we make sure we're still proud of this work in 2050?"
  },
  {
    id: 4,
    name: "Matthias Weber",
    role: "Project Director, Central European Infrastructure Coalition",
    era: "The European Expansion",
    period: "2008–2015",
    relationship: "Industry Partner",
    image: null,
    testimonial: "I've worked with dozens of engineering firms across Europe. Redspire under Ruben's leadership was different. They didn't just deliver technical solutions—they delivered strategic clarity. When we faced a politically contentious bridge replacement in 2011, other firms gave us three options. Ruben gave us a framework for making the decision ourselves. He understood that his job wasn't to be the smartest person in the room. It was to make everyone in the room smarter.",
    highlight: "He understood his job wasn't to be the smartest person in the room."
  },
  {
    id: 5,
    name: "Jennifer Martinez",
    role: "Executive Director, Utah Transit Authority",
    era: "The American Chapter",
    period: "2016–2020",
    relationship: "Client Partner",
    image: null,
    testimonial: "When Ruben relocated Redspire to Utah, frankly, we were skeptical. Another European consultant promising to understand American infrastructure? But within three months, he'd identified inefficiencies in our capital planning process that we'd overlooked for years. He didn't criticize—he asked questions that made us rethink everything. Four years later, we've delivered more projects under budget and ahead of schedule than any comparable period in our history. That's the Ruben effect.",
    highlight: "He didn't criticize—he asked questions that made us rethink everything."
  },
  {
    id: 6,
    name: "David Chen",
    role: "Structural Engineer, Redspire Civil Engineering",
    era: "The Mentorship",
    period: "2019–Present",
    relationship: "Mentee",
    image: null,
    testimonial: "I joined Redspire as a junior engineer terrified of making mistakes. Ruben told me something I'll never forget: 'Every bridge we build will be inspected by engineers who aren't born yet. They won't care about our deadlines or our budgets. They'll care whether we did it right.' That changed how I think about my work. He doesn't just teach engineering—he teaches responsibility across generations. When I review my calculations now, I imagine those future engineers looking at my work. That's what Ruben gave me.",
    highlight: "He doesn't just teach engineering—he teaches responsibility across generations."
  },
  {
    id: 7,
    name: "Prof. Margaret Sullivan",
    role: "Dean of Engineering, Northeastern University",
    era: "The Recognition",
    period: "2021–Present",
    relationship: "Academic Peer",
    image: null,
    testimonial: "I've invited Ruben to speak to our graduate students three times now, and each time, the response is extraordinary. He doesn't present PowerPoints about his awards. He tells stories about failures, about teams, about the unglamorous work of getting things right. Last year, a student asked him what he's most proud of. His answer? The engineers he's trained who are now leading their own projects. Not the bridges. Not the recognition. The people. That's the mark of someone who understands what legacy actually means.",
    highlight: "Not the bridges. Not the recognition. The people."
  }
];

// Testimonial Card Component
const TestimonialCard = ({ testimonial, index }) => {
  const isEven = index % 2 === 0;
  
  return (
    <div className={`journey-testimonial ${isEven ? 'journey-left' : 'journey-right'}`}>
      <div className="journey-connector">
        <div className="journey-era-badge">
          <Calendar size={16} />
          <span>{testimonial.period}</span>
        </div>
      </div>
      
      <div className="journey-card">
        <div className="journey-era-label">{testimonial.era}</div>
        
        <div className="journey-quote-icon">
          <Quote size={32} />
        </div>
        
        <blockquote className="journey-quote">
          {testimonial.testimonial}
        </blockquote>
        
        {testimonial.highlight && (
          <div className="journey-highlight">
            "{testimonial.highlight}"
          </div>
        )}
        
        <div className="journey-author">
          <div className="journey-author-avatar">
            {testimonial.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div className="journey-author-info">
            <h4 className="journey-author-name">{testimonial.name}</h4>
            <p className="journey-author-relationship">{testimonial.relationship}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CollectiveJourney = () => {
  return (
    <div className="collective-journey-page">
      {/* Hero Section */}
      <section className="journey-hero">
        <div className="container">
          <div className="journey-hero-badge">
            <Users size={20} />
            <span>Voices from Three Decades</span>
          </div>
          
          <h1 className="journey-hero-title">The Collective Journey</h1>
          
          <p className="journey-hero-subtitle">
            Three Decades of Leadership, Innovation, and Friendship
            <br />
            <em>As Told by Those Who Were There</em>
          </p>
          
          <div className="journey-hero-content">
            <p>
              Every career is measured in milestones—degrees earned, firms founded, projects delivered, 
              awards received. But the true measure of a professional life isn't found in résumés or 
              LinkedIn profiles. It lives in the memories of colleagues who witnessed pivotal moments, 
              mentors who saw potential before it was proven, and teams who turned vision into reality.
            </p>
            <p>
              This is Ruben P. Wargner's story—not as he would tell it, but as those who walked beside 
              him remember it. From a curious student in Prague asking impossible questions about 
              generational infrastructure, to a globally recognized strategist reshaping how nations 
              approach public works, Ruben's journey has been witnessed, shaped, and celebrated by 
              remarkable people.
            </p>
            <p>
              These are their voices. These are their memories. This is the collective journey of 
              building not just structures, but legacies—together.
            </p>
          </div>
          
          <div className="journey-hero-icon">
            <Heart size={40} />
          </div>
        </div>
      </section>
      
      {/* Timeline Section */}
      <section className="journey-timeline-section">
        <div className="container">
          <div className="journey-timeline-wrapper">
            <div className="journey-timeline-line"></div>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} index={index} />
            ))}
          </div>
        </div>
      </section>
      
      {/* Closing Section */}
      <section className="journey-closing">
        <div className="container">
          <div className="journey-closing-content">
            <Quote size={48} className="journey-closing-icon" />
            <p className="journey-closing-text">
              "The best engineers don't just build infrastructure. They build teams, cultures, and futures. 
              Ruben has spent three decades proving that the most important blueprint isn't drawn on paper—it's 
              written in the people you invest in along the way."
            </p>
            <p className="journey-closing-attribution">
              — The Collective
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CollectiveJourney;
